import EmptyState from "@/components/EmptyState";
import ImageWithSpinner from "@/components/ImageWithSpinner";
import ScrollTopButton from "@/components/ScrollTopButton";
import Spinner from "@/components/Spinner";
import { useInfiniteExplore, usePrefetchData } from "@/tanstack-hooks";
import { CloudOffIcon, Compass, RefreshCwIcon } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useToast } from "@/hooks/use-toast";
import AppInstallDialog from "@/components/AppInstallDialog";
import { useTranslation } from "@/hooks/useTranslation";
import EnableNotificationsDialog from "@/components/EnableNotificationsDialog";
import PulseLoader from "@/components/PulseLoader";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import { PageType } from "@/components/PageType";

function ExplorePage() {
  const { toast } = useToast();
  const { ref, inView } = useInView({
    rootMargin: "400px 0px", // Starts observing 200px before the element comes into view
    threshold: 0,
  });
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteExplore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const pageRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  usePrefetchData(data !== undefined);
  const isStandalone = useStandaloneMode();
  const t = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleRefresh = async (): Promise<void> => {
    try {
      await refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.EXPLORE_PAGE.ERRORS.REFRESH,
        description: t.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    }
  };

  const handleImageClick = (userId: string) => {
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
    navigate(`/profile/${userId}`, {
      state: { from: location.pathname },
    });
  };

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(location.pathname);
    if (savedScroll !== null) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(location.pathname);
        setIsLoaded(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If no saved scroll, we can show content immediately
      setIsLoaded(true);
    }
  }, [data]);

  // Debounced fetch next page
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] w-full mx-auto">
        <PulseLoader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.EXPLORE_PAGE.ERRORS.LOADING}
        message={t.EXPLORE_PAGE.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  const images = data.pages.flatMap((page) => page.items);

  if (images.length === 0) {
    return (
      <EmptyState
        icon={Compass}
        title={t.EXPLORE_PAGE.LABELS.EMPTY_TITLE}
        message={t.EXPLORE_PAGE.LABELS.EMPTY_DESCRIPTION}
      />
    );
  }

  const pageContent = (
    <div
      className={`flex flex-col dark w-full max-w-5xl select-none ${
        isStandalone ? "pb-14" : "pb-10"
      } mx-auto transition-opacity duration-50 ${
        isLoaded ? "opacity-100" : "opacity-0"
      }`}
      ref={pageRef}
    >
      <div className="grid grid-cols-3 md:grid-cols-4 gap-1">
        {images.map((image) => (
          <ImageWithSpinner
            src={image.photo_url}
            imgId={image.user_id}
            key={image.user_id}
            className="object-cover hover:cursor-pointer"
            onClick={() => handleImageClick(image.user_id)}
          />
        ))}
      </div>

      <div ref={ref} className="w-full h-20 flex items-center justify-center">
        {isFetchingNextPage && <Spinner />}
      </div>
      <ScrollTopButton />
    </div>
  );

  return (
    <>
      {isMobile ? (
        <PullToRefresh
          onRefresh={handleRefresh}
          pullingContent={
            <div className="flex justify-center items-center w-full">
              <RefreshCwIcon />
            </div>
          }
          pullDownThreshold={100}
          maxPullDownDistance={120}
          resistance={2.5}
          refreshingContent={<Spinner />}
        >
          {pageContent}
        </PullToRefresh>
      ) : (
        pageContent
      )}
      <AppInstallDialog pageType={PageType.EXPLORE} />
      <EnableNotificationsDialog pageType={PageType.EXPLORE} />
    </>
  );
}

export default memo(ExplorePage);
